import __isEqual from 'lodash/isEqual'
import { connect } from 'react-redux'

import { filtersActions, TAX_LIST_INITIAL_FILTERS } from '@services'

import { usePageConfig } from '@contexts/PageControllerProvider'
import { usePortal } from '@contexts/PortalProvider'

import { TAX_LIST_DATE_FIELD_OPTIONS } from '@components/pages/constants'

import type { FilterBarBaseProps } from './FilterBar'
import { FilterBar } from './FilterBar'
import { getSelectableFilterProps, getStatusFilterDialogProps } from './helpers'

import { TagFilterEmptyPlaceholderMessage, TagFilterLabelMessage, TaxListPageMessage } from '@messages'

interface PureTaxListPageFilterBarProps extends FilterBarBaseProps {
  filters: TaxListStoreFilters
  tagOptions: Tag[]
}

function PureTaxListPageFilterBar({
  filters,
  filters: {
    dateField,
    expiredDays,
    expiringDays,
    isExpired,
    isExpiring,
    isPaid,
    search,
    searchFields,
    withoutTagIds,
    withTagIds,
  },
  isDateFilterDisabled,
  resetFilters,
  tagOptions,
  updateFilters,
}: PureTaxListPageFilterBarProps) {
  const { setPortalAnchorEl } = usePortal()
  const pageConfig = usePageConfig()

  const isDateFilterUsed = true
  const searchFilterProps = {
    search,
    searchFields,
    options: pageConfig.searchFilterProps?.config.options ?? [],
  }

  const paidStatusFilterProps = {
    values: {
      expiredDays,
      expiringDays,
      isExpired,
      isExpiring,
      isPaid,
    },
  }

  const selectableFilterProps = getSelectableFilterProps({
    values: {
      withTagIds,
    },
    config: [
      {
        keyValue: 'withTagIds' as const,
        options: tagOptions,
        labelText: TagFilterLabelMessage,
        emptyPlaceholder: TagFilterEmptyPlaceholderMessage,
      },
    ],
  })

  const filterDialogProps: FilterDialogProps<StatusFilterConfig> = {
    dateFilterProps: {
      dateTypeOptions: TAX_LIST_DATE_FIELD_OPTIONS,
      dateTypeValue: dateField,
    },
    isChanged: !__isEqual(filters, TAX_LIST_INITIAL_FILTERS),
    page: TaxListPageMessage,
    paidStatusFilterProps,
    searchFilterProps,
  }

  if (pageConfig.statusFilterProps) {
    filterDialogProps['statusFilterProps'] = getStatusFilterDialogProps({
      config: pageConfig.statusFilterProps.config,
      values: filters,
    })
  }
  if (pageConfig.tagFilterProps) {
    filterDialogProps['tagFilterProps'] = {
      config: {
        ...pageConfig.tagFilterProps.config,
        options: tagOptions,
      },
      values: {
        include: withTagIds,
        exclude: withoutTagIds,
      },
    }
  }
  if (pageConfig.amountFilterProps) {
    filterDialogProps['amountFilterProps'] = {
      // config: pageConfig.amountFilterProps.config,
      grossAmountMax: filters.grossAmountMax,
      grossAmountMin: filters.grossAmountMin,
    }
  }

  return (
    <FilterBar
      dateField={filters.dateField}
      filterDialogProps={filterDialogProps}
      isDateFilterDisabled={isDateFilterDisabled}
      isDateFilterUsed={isDateFilterUsed}
      paidStatusFilterProps={paidStatusFilterProps}
      resetFilters={resetFilters}
      searchFilterProps={searchFilterProps}
      selectableFilterProps={selectableFilterProps}
      setPortalRef={setPortalAnchorEl}
      updateFilters={updateFilters}
    />
  )
}

export const TaxListPageFilterBar = connect(
  (state: Store) => {
    const {
      dashboard: {
        tags: { data: tagOptions },
      },
      filters: { isGlobalDateFilterDisabled, taxList },
    } = state
    return {
      filters: taxList,
      isDateFilterDisabled: isGlobalDateFilterDisabled,
      tagOptions,
    }
  },
  {
    resetFilters: filtersActions.resetTaxListFilters.request,
    updateFilters: filtersActions.updateTaxListFilters.request,
  }
)(PureTaxListPageFilterBar)

TaxListPageFilterBar.displayName = 'TaxListPageFilterBar'
